import React from "react";

import FullScreenAuthLoader from "@/ui/organisms/FullScreenAuthLoader";

type TAvailableRole = "admin" | "analyst";

export type ViewAuthRoleGuardParams = {
    useState: () => {
        userRole: TAvailableRole | null;
        isUserRoleLoading: boolean;
        isProdaUser: boolean;
    };
    useCases: () => {
        redirectOnRoleNotMatched: () => void;
    };
};

export type ViewAuthRoleGuardProps = {
    component: React.ComponentType;
    allowedRoles: TAvailableRole[];
    prodaUserOnly?: boolean;
};

export const createViewAuthRoleGuard = ({
    useState,
    useCases,
}: ViewAuthRoleGuardParams) => {
    const ViewAuthRoleGuard: React.FC<ViewAuthRoleGuardProps> = props => {
        const { allowedRoles, component: Component, prodaUserOnly } = props;

        const { userRole, isUserRoleLoading, isProdaUser } = useState();
        const { redirectOnRoleNotMatched } = useCases();

        const userRoleIsMatched = React.useMemo(() => {
            if (isUserRoleLoading) return false;
            if (!userRole) return false;
            return allowedRoles.includes(userRole);
        }, [allowedRoles, userRole, isUserRoleLoading]);

        React.useEffect(() => {
            if (isUserRoleLoading) return;
            if (!userRole) return;

            if (!userRoleIsMatched) {
                redirectOnRoleNotMatched();
            }
            if (prodaUserOnly === true && !isProdaUser) {
                redirectOnRoleNotMatched();
            }
        }, [
            isUserRoleLoading,
            userRole,
            userRoleIsMatched,
            redirectOnRoleNotMatched,
            prodaUserOnly,
            isProdaUser,
        ]);

        if (isUserRoleLoading) {
            return <FullScreenAuthLoader label={"Authenticating..."} />;
        }

        if (!isUserRoleLoading && !userRoleIsMatched) {
            return <FullScreenAuthLoader label={"Redirecting..."} />;
        }

        return <Component />;
    };

    return ViewAuthRoleGuard;
};
